/**
 * Find the search terms on the page, and walk between them.
 *
 * The hard part is not the finding, it is the TIMING. A result click navigates
 * to a screen that then fetches its model, fetches its record, renders a form
 * and lazily renders a document page — so the content the reader is looking
 * for does not exist at the moment the highlighter first runs, and a one-shot
 * scan reliably reports "0 matches" on a page that is full of them.
 *
 * So the scan is driven by a `MutationObserver` over the root, debounced: every
 * time the page settles, the occurrence list is rebuilt. `childList` and
 * `characterData` only — never `attributes` — because the fallback highlighter
 * writes an attribute, and observing attributes would make it re-trigger
 * itself forever.
 *
 * The active index is kept STABLE across rescans where it can be: content
 * arriving below the reader must not silently move "match 3 of 7" to a
 * different match under their cursor.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  type Occurrence,
  applyHighlights, clearHighlights, collectOccurrences,
  ensureHighlightStyles, highlightSupported, scrollToOccurrence,
} from './highlight'

/** How long the page must stay still before a rescan. */
const SETTLE_MS = 220

/**
 * Rescans stop once the page has been quiet this long. A screen that
 * re-renders on a timer (a live dashboard tile, a polling list) would
 * otherwise keep the observer scanning the whole document for as long as the
 * highlight is active.
 */
const MAX_RESCAN_MS = 20000

/** Lets `rescan()` reopen the window after MAX_RESCAN_MS has elapsed. */
const RESCAN_EVENT = 'twasta:search-rescan'

export interface SearchHighlightApi {
  /** Occurrences currently on the page. */
  count: number
  /** 0-based index of the active occurrence, or -1 when there are none. */
  index: number
  next: () => void
  previous: () => void
  /** Re-scan now — for a host that knows content just arrived. */
  rescan: () => void
  /**
   * False when the browser cannot paint word-level highlights and the terms
   * are marked at field level instead. Surfaced so the UI can say so rather
   * than let the reader wonder why the words are not coloured.
   */
  wordLevel: boolean
}

export function useSearchHighlight(
  terms: string[],
  options: { root?: HTMLElement | null; enabled?: boolean } = {},
): SearchHighlightApi {
  const { root = null, enabled = true } = options
  const [occurrences, setOccurrences] = useState<Occurrence[]>([])
  const [index, setIndex] = useState(-1)
  const [wordLevel, setWordLevel] = useState(true)
  const scanRef = useRef<() => void>(() => {})
  // Terms arrive as a fresh array on every render of the caller; comparing the
  // joined string keeps the effects from restarting on identical terms.
  const key = useMemo(() => terms.join(' '), [terms])
  const termsRef = useRef(terms)
  termsRef.current = terms

  const scan = useCallback(() => {
    if (typeof document === 'undefined') return
    const host = root || document.body
    const active = termsRef.current
    if (!host || !active.length || !enabled) {
      setOccurrences([])
      setIndex(-1)
      return
    }
    const found = collectOccurrences(host, active)
    setOccurrences(found)
    setIndex((prev) => {
      if (!found.length) return -1
      if (prev < 0) return 0
      return Math.min(prev, found.length - 1)
    })
  }, [root, key, enabled])

  scanRef.current = scan

  // Scan on mount / term change, then on every settled mutation.
  useEffect(() => {
    if (typeof document === 'undefined') return undefined
    if (!enabled || !key) {
      clearHighlights()
      setOccurrences([])
      setIndex(-1)
      return undefined
    }
    ensureHighlightStyles()
    const host = root || document.body
    if (!host) return undefined

    let timer: number | undefined
    let stopAt = Date.now() + MAX_RESCAN_MS
    const settle = () => {
      window.clearTimeout(timer)
      if (Date.now() > stopAt) return
      timer = window.setTimeout(() => scanRef.current(), SETTLE_MS)
    }
    scanRef.current()
    const observer = new MutationObserver(settle)
    observer.observe(host, { childList: true, characterData: true, subtree: true })
    const extend = () => { stopAt = Date.now() + MAX_RESCAN_MS }
    window.addEventListener(RESCAN_EVENT, extend)
    return () => {
      window.clearTimeout(timer)
      observer.disconnect()
      window.removeEventListener(RESCAN_EVENT, extend)
      clearHighlights()
    }
  }, [root, key, enabled])

  // Paint whenever the set or the active member changes.
  useEffect(() => {
    if (!enabled || !occurrences.length) {
      clearHighlights()
      return
    }
    setWordLevel(applyHighlights(occurrences, index) && highlightSupported())
  }, [occurrences, index, enabled])

  const move = useCallback((delta: number) => {
    setIndex((prev) => {
      if (!occurrences.length) return -1
      const next = (prev + delta + occurrences.length) % occurrences.length
      // Scrolling is a side effect of MOVING, and belongs with the move rather
      // than in an effect that would also fire on a rescan and yank the page
      // back every time the screen re-rendered.
      scrollToOccurrence(occurrences[next])
      return next
    })
  }, [occurrences])

  const next = useCallback(() => move(1), [move])
  const previous = useCallback(() => move(-1), [move])
  const rescan = useCallback(() => {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event(RESCAN_EVENT))
    }
    scanRef.current()
  }, [])

  return { count: occurrences.length, index, next, previous, rescan, wordLevel }
}
